import { ArrowLeft, Heart, Clock, Users, Tag, Lightbulb, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type RecipeData, categoryLabels } from '@/data/mockData';

interface RecipeDetailViewProps {
  recipe: RecipeData;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onBack: () => void;
}

const RecipeDetailView = ({ recipe, isFavorite, onToggleFavorite, onBack }: RecipeDetailViewProps) => (
  <div className="p-4 md:p-8 max-w-md mx-auto space-y-5 animate-fade-in pb-24">
    {/* Top bar — pr-14 avoids FloatingProfile overlap */}
    <div className="flex items-center pr-14">
      <button onClick={onBack} className="text-sm text-primary font-medium flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Volver
      </button>
    </div>

    {/* Header with inline favorite button */}
    <div>
      <div className="flex items-start gap-2">
        <h1 className="text-2xl font-heading font-bold leading-tight flex-1">{recipe.title}</h1>
        <button
          onClick={onToggleFavorite}
          className={cn(
            "flex-shrink-0 mt-1 p-2 rounded-full transition-colors",
            isFavorite ? "bg-accent/10" : "bg-muted"
          )}
        >
          <Heart className={cn("h-5 w-5", isFavorite ? "fill-accent text-accent" : "text-muted-foreground")} />
        </button>
      </div>
      <p className="text-sm text-muted-foreground mt-1">{recipe.description}</p>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" /> {recipe.prepTime}
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5" /> {recipe.servings} {recipe.servings === 1 ? 'porción' : 'porciones'}
        </span>
        <span className="flex items-center gap-1 text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">
          <Tag className="h-3 w-3" /> {recipe.benefitTag}
        </span>
      </div>
    </div>

    {/* Why it helps */}
    <div className="bg-secondary/60 rounded-2xl p-4 border border-primary/10">
      <div className="flex items-center gap-2 mb-1.5">
        <Lightbulb className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-secondary-foreground">¿Por qué te conviene?</h3>
      </div>
      <p className="text-sm text-secondary-foreground/80 leading-relaxed">{recipe.whyItHelps}</p>
    </div>

    {/* Ingredients */}
    <div className="bg-card rounded-2xl p-5 shadow-card space-y-3">
      <h3 className="text-sm font-medium">Ingredientes</h3>
      <ul className="space-y-2">
        {recipe.ingredients.map((ing, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
            {ing}
          </li>
        ))}
      </ul>
    </div>

    {/* Preparation */}
    <div className="bg-card rounded-2xl p-5 shadow-card space-y-3">
      <h3 className="text-sm font-medium">Modo de Preparación</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{recipe.preparation}</p>
    </div>

    {/* Substitutions */}
    {recipe.substitutions.length > 0 && (
      <div className="bg-card rounded-2xl p-5 shadow-card space-y-3">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-wellness-peach" />
          <h3 className="text-sm font-medium">Sustituciones simples</h3>
        </div>
        <ul className="space-y-2">
          {recipe.substitutions.map((sub, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="w-1 h-1 bg-wellness-peach rounded-full flex-shrink-0" />
              {sub}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Usage context */}
    <div className="bg-wellness-peach-light/60 rounded-2xl p-4 border border-wellness-peach/20">
      <p className="text-xs font-semibold text-foreground mb-0.5">💡 Cuándo usarla</p>
      <p className="text-sm text-muted-foreground">{recipe.usageContext}</p>
    </div>
  </div>
);

export default RecipeDetailView;
